import type { FC, KeyboardEventHandler } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'next-i18next';
import type { SearchAutocompleteSB } from '@mwl/core-lib';
import { debounceFn, searchAutocompleteSB, sendAnalyticsData, usePrevious, useSportUrlParams } from '@mwl/core-lib';
import SearchIcon from '@public/assets/common/icons/search.svg';
import { Button } from '@/components/Button/Button';
import { FormFieldSearch } from '@/components/FormFieldSearch/FormFieldSearch';
import { useMediaWidthDown } from '@/hooks/useMediaWidthDown';
import { SportSearchAutocomplete } from '../SportSearchAutocomplete/SportSearchAutocomplete';
import { sportSearchAnalytics } from './SportSearchInput.analystics';
import { useSearchSuggestionsKeyboardNavigation } from './SportSearchInput.hooks';
import type { SportSearchInputProps } from './SportSearchInput.types';
import styles from './SportSearchInput.module.scss';
const requestDebounceTime = 400;
const maxInputLength = 30;
export const SportSearchInput: FC<SportSearchInputProps> = ({
  className,
  searchRef,
  searchViewState,
  handleOpenSearch,
  isMobile,
  handleSubmitSearch,
  handleUpdateSearchView,
  inputValue,
  handleChangeInputValue
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  // Флаг для отслеживания программного изменения inputValue (оно не должно вызывать onChange)
  const isProgrammaticChangeRef = useRef(false);
  const {
    search
  } = useSportUrlParams();
  const {
    t
  } = useTranslation('sport');
  const isXsScreen = useMediaWidthDown('screen850');
  const prevSearch = usePrevious(search);
  const [suggestions, setSuggestions] = useState<SearchAutocompleteSB>([]);
  const [selectedSuggestionValue, setSelectedSuggestionValue] = useState('');
  const {
    activeElement,
    onKeyDown,
    resetActiveElement
  } = useSearchSuggestionsKeyboardNavigation(suggestions.length);
  const handleFocusSearch = () => {
    handleOpenSearch();
    sendAnalyticsData(isXsScreen ? sportSearchAnalytics.mobile_select.eventName : sportSearchAnalytics.select.eventName, {
      cls: inputRef.current?.className
    });
  };
  const handleFetchSuggestions = useCallback(async (value: string) => {
    const {
      data
    } = await searchAutocompleteSB(value, {
      signal: abortControllerRef.current?.signal
    });
    const sortedData = [...(data ?? [])].sort((a, b) => a.value.localeCompare(b.value));
    if (sortedData) {
      setSelectedSuggestionValue('');
      setSuggestions(sortedData.slice(0, 5));
    }
  }, []);
  const debounceSuggestions = useMemo(() => debounceFn<string>(value => {
    handleFetchSuggestions(value);
  }, requestDebounceTime), [handleFetchSuggestions]);
  const handleChange = useCallback((value: string) => {
    if (isProgrammaticChangeRef.current) {
      isProgrammaticChangeRef.current = false;
      return;
    }
    if (value.length > 0) {
      handleUpdateSearchView('typing');
    }
    handleChangeInputValue(value);
    if (value.length > 0 && value !== selectedSuggestionValue) {
      debounceSuggestions(value);
    }
  }, [debounceSuggestions, selectedSuggestionValue, handleUpdateSearchView, handleChangeInputValue]);
  const handleSelectSuggestion = useCallback((value: string) => {
    setSelectedSuggestionValue(value);
    handleSubmitSearch(value);
    abortControllerRef.current?.abort();
    isProgrammaticChangeRef.current = true;
    handleChangeInputValue(value);
  }, [handleSubmitSearch, handleChangeInputValue]);
  const handleSubmit = useCallback((localValue?: string) => {
    handleSubmitSearch(localValue || inputValue || '');
    setSelectedSuggestionValue(localValue || inputValue || '');
    abortControllerRef.current?.abort();
  }, [handleSubmitSearch, inputValue]);
  const handleKeyDown = useCallback<KeyboardEventHandler<HTMLDivElement>>(event => {
    if (event.key === 'Enter') {
      if (typeof activeElement === 'number') {
        handleSubmit(suggestions[activeElement].value);
        resetActiveElement();
      } else {
        // если очень быстро нажать enter, то state не успевает обновиться и inputValue будет пустой, поэтому читаем из input напрямую
        const safetyInputValue = inputRef.current?.value || '';
        handleSubmit(safetyInputValue);
      }
    } else {
      onKeyDown(event);
    }
  }, [handleSubmit, activeElement, suggestions, onKeyDown, resetActiveElement]);
  const handleClear = useCallback(() => {
    handleChangeInputValue('');
    sendAnalyticsData(sportSearchAnalytics.sportTopFilter_searchClear_xIcon.click.eventName, {
      cls: styles.searchField
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handleChangeInputValue]);
  useEffect(() => {
    if (inputValue.length === 0) {
      setSuggestions([]);
    }
  }, [inputValue.length]);
  useEffect(() => {
    abortControllerRef.current = new AbortController();
  }, [searchViewState]);
  useEffect(() => {
    if (prevSearch !== search) {
      isProgrammaticChangeRef.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);
  return <div ref={searchRef} role="button" tabIndex={0} className={cn(className, {
    [styles.root]: searchViewState === 'open'
  })} onKeyDown={handleKeyDown} data-sentry-component="SportSearchInput" data-sentry-source-file="SportSearchInput.tsx">
      <div className={cn(styles.input, {
      [styles.bottomInput]: searchViewState === 'typing' && inputValue && suggestions.length
    })}>
        <FormFieldSearch ref={inputRef} value={inputValue} className={styles.searchField} onChange={handleChange} onClear={handleClear} onFocus={handleFocusSearch} maxInputLength={maxInputLength} debounceMs={requestDebounceTime} classes={{
        componentWrapper: styles.componentWrapper,
        component: cn(styles.component, {
          [styles.componentDefault]: searchViewState === 'default'
        })
      }} shouldHandleTransliteration={false} data-sentry-element="FormFieldSearch" data-sentry-source-file="SportSearchInput.tsx" />
        {(searchViewState !== 'default' || inputValue.length > 0) && <Button as="button" variant="filled" color="red" size="sm" rounded aria-label="search" className={styles.buttonSubmit} analytics={sportSearchAnalytics?.search_btn_click} onClick={() => handleSubmit()}>
            <SearchIcon width={20} height={20} />

            {!isMobile && <p className={styles.label}>{t('search.button', 'Search')}</p>}
          </Button>}
      </div>
      {inputValue && searchViewState === 'typing' && <SportSearchAutocomplete handleSearchClick={handleSelectSuggestion} searchResult={suggestions} activeElement={activeElement} />}
    </div>;
};